import { HttpParams } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { DocumentRecord } from "@models/annual-statements/document-record";
import { ChangeAuthorisation } from "@models/documents/change-authorisation";
import { Document } from "@models/documents/document";
import { DocumentAuthorise } from "@models/documents/document-authorise";
import { DocumentNotificationDetails } from "@models/documents/document-notification-details";
import { DocumentSelection, DocumentSelectionDto } from "@models/documents/document-selection";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { LodgementDeadline } from "@models/documents/lodgement-deadline";
import { ICreateDocumentResponse } from "@models/requests/create-document-response";
import { PageResult } from "@models/requests/page-result.interface";
import BaseApiService from "@shared/services/base/base-api.service";
import { IServerSideGetRowsRequest } from "ag-grid-enterprise";
import { catchError, combineLatest, delay, map, Observable, of } from "rxjs";
import { environment } from "../../../environments/environment";

@Injectable({ providedIn: 'root' })
export class DocumentsApiService extends BaseApiService {
  createOrUpdateDocument(document: Document, saveAndCompleteLater = false): Observable<ICreateDocumentResponse> {
    const options = { params: { saveAndCompleteLater } };
    return this.api.post<ICreateDocumentResponse>(`${ environment.api_url }/documents`, Document.prepareToRequest(document), options);
  }

  authoriseDocument(documentId: string, body: ChangeAuthorisation): Observable<DocumentAuthorise | null> {
    return this.api.post<DocumentAuthorise>(`${ environment.api_url }/documents/${ documentId }/authorise`, body).pipe(
      catchError(() => of(null))
    );
  }

  confirmDocument(documentId: string): Observable<Document> {
    return this.api.post<Document>(`${ this.apiUrl }/documents/${ documentId }/confirm`, {});
  }

  getById(documentId: string): Observable<Document> {
    return this.api.get<Document>(`${ this.apiUrl }/documents/${ documentId }`);
  }

  lodgeDocument(documentId: string[], merge = true): Observable<string[]> {
    return this.api.post<string[]>(`${ this.apiUrl }/documents/lodge?merge=${ merge }`, documentId);
  }

  getDocumentList(request: IServerSideGetRowsRequest, search?: string): Observable<PageResult<DocumentRecord>> {
    let params = new HttpParams();
    if (search) {
      params = params.append('search', search);
    }

    return this.api.post<PageResult<DocumentRecord>>(`${ this.apiUrl }/documents/grid`, request, { params: params })
      .pipe(map(data => {
        data.records = data.records.map(d => new DocumentRecord(d));
        return data;
      }));
  }

  getEntityPendingDocuments(companyId: string): Observable<PageResult<DocumentRecord>> {
    const pendingDocumentsStatuses: DocumentStatusEnum[] = [
      DocumentStatusEnum.Draft,
      DocumentStatusEnum.AuthorisationPending,
      DocumentStatusEnum.SignaturePending,
      DocumentStatusEnum.LodgementPending
    ];

    const request: unknown = {
      startRow: 0,
      endRow: 100,
      filterModel: {
        entityId: {
          filterType: 'set',
          values: [companyId]
        },
        status: {
          filterType: 'set',
          values: pendingDocumentsStatuses.map((status) => status.toString())
        }
      }
    };

    return this.getDocumentList(request as IServerSideGetRowsRequest)
      .pipe(
        map((result) => {
          result.records.sort((a, b) => {
            return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
          });

          return result;
        })
      );
  }


  updateAuthorisation(documentId: string, body: ChangeAuthorisation): Observable<DocumentAuthorise | null> {
    return this.api.post<DocumentAuthorise>(`${ this.apiUrl }/documents/${ documentId }/authorisation`, body)
      .pipe(
        catchError(() => of(null))
      );
  }

  mergeDocuments(documentIds: string[]): Observable<{ documentId: string } | null> {
    return this.api.post<{ documentId: string }>(`${ this.apiUrl }/documents/merge`, documentIds);
  }

  deleteDocument(documentId: string): Observable<void> {
    return this.api.delete<void>(`${ this.apiUrl }/documents/${ documentId }`);
  }

  deleteDocumentsBulk(documentsId: string[]): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/documents/bulk/delete`, documentsId);
  }

  markAsLodge(documentsId: string[], asicNumber: string): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/documents/mark-as-lodged?asicNumber=${ asicNumber }`, documentsId);
  }

  getDocumentsSelection(documentId: string): Observable<DocumentSelection[]> {
    return this.api.post<DocumentSelectionDto>(`${ this.apiUrl }/documents/${ documentId }/selection`, {})
      .pipe(
        map(r => r.selection),
      );
  }

  getLodgementDeadline(documentIds: string[]): Observable<LodgementDeadline> {
    return this.api.post<LodgementDeadline>(`${ this.apiUrl }/documents/lodgement-deadline`, documentIds);
  }

  updateSignDate(documentsId: string[], signDate: string): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/documents/sign-date?signDate=${ signDate }`, documentsId);
  }

  updateNotificationDetails(documentId: string, details: DocumentNotificationDetails): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/documents/${ documentId }/notification-details`, details);
  }

  resetDocumentSendViaEmail(documentId: string): Observable<Document> {
    return this.api.post<Document>(`${ this.apiUrl }/documents/${ documentId }/reset`, null);
  }

  // Bulk
  createOrUpdateBulkDocuments(documents: Document[], saveAndCompleteLater = false): Observable<(Document | null)[]> {
    const createDocumentRequestsGroups = documents.map(((document) => {

      return this.createOrUpdateDocument(document, saveAndCompleteLater)
        .pipe(
          map(({ id }) => {
            document.documentId = id;
            return document;
          }),
          catchError((error) => {
            console.warn('[createOrUpdateBulkDocuments] error: ', error);
            return of(null);
          })
        );
    }));

    const createDocumentRequestsGroupsAAAWithDelays = createDocumentRequestsGroups.map((request, index) => {
      if ((index % 10 == 0) && index != 0) {
        // delay for every 10th item
        return request.pipe(delay(1000));
      }

      return request;
    });

    return combineLatest(createDocumentRequestsGroupsAAAWithDelays);
  }

  authoriseBulkDocuments(documentsWithChangeAuthorization: Document[]): Observable<(DocumentAuthorise | null)[]> {
    const changeAuthRequests = documentsWithChangeAuthorization.map((document) => {
      return this.authoriseDocument(document.documentId, document.changeAuthorisation!)
        .pipe(
          catchError((error) => {
            console.warn('[authoriseBulkDocuments] error: ', error);
            return of(null);
          })
        );
    });

    const changeAuthRequestsWithDelay = changeAuthRequests.map((changeAuthRequest, index) => {
      if ((index % 10 == 0) && index != 0) {
        // delay for every 10th item
        return changeAuthRequest.pipe(delay(1000));
      }

      return changeAuthRequest;
    });

    return combineLatest(changeAuthRequestsWithDelay);
  }
}
